// API client. All calls go through REACT_APP_BACKEND_URL with '/api' prefix.
const BASE = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'yc_token';

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

export class ApiError extends Error {
  constructor(status, detail) {
    super(typeof detail === 'string' ? detail : 'Request failed');
    this.status = status;
    this.detail = detail;
    this.isPaywall = status === 402 && detail && typeof detail === 'object' && detail.paywall;
  }
}

function formatDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  if (typeof detail === 'object' && detail.message) return detail.message;
  return JSON.stringify(detail);
}

async function request(method, path, body) {
  const headers = { 'Content-Type': 'application/json' };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const resp = await fetch(`${BASE}/api${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try { data = await resp.json(); } catch (e) { /* empty body */ }
  if (!resp.ok) {
    const err = new ApiError(resp.status, data?.detail ?? data);
    err.message = err.isPaywall ? data.detail.message : formatDetail(data?.detail ?? data);
    if (resp.status === 401 && token && !path.startsWith('/auth/login')) {
      clearToken();
    }
    throw err;
  }
  return data;
}

const api = {
  get: (path) => request('GET', path),
  post: (path, body) => request('POST', path, body),
  patch: (path, body) => request('PATCH', path, body),
  del: (path) => request('DELETE', path),
  upload: (path, blob, field = 'audio', filename = 'audio') => uploadRequest(path, blob, field, filename),
  uploadStream: (path, blob, field, filename, onEvent) => uploadStreamRequest(path, blob, field, filename, onEvent),
};

// Streamed upload: POST a blob, read back NDJSON events (one JSON object per line).
// If the server answers with plain JSON (e.g. no_speech), it's returned directly.
async function uploadStreamRequest(path, blob, field, filename, onEvent) {
  const headers = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const form = new FormData();
  form.append(field, blob, filename);
  const resp = await fetch(`${BASE}/api${path}`, { method: 'POST', headers, body: form });
  if (!resp.ok) {
    let data = null;
    try { data = await resp.json(); } catch (e) { /* empty body */ }
    const err = new ApiError(resp.status, data?.detail ?? data);
    err.message = err.isPaywall ? data.detail.message : formatDetail(data?.detail ?? data);
    throw err;
  }
  const ctype = resp.headers.get('content-type') || '';
  if (ctype.includes('application/json')) return resp.json();
  const reader = resp.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf('\n')) >= 0) {
      const line = buf.slice(0, idx).trim();
      buf = buf.slice(idx + 1);
      if (line) {
        try { onEvent(JSON.parse(line)); } catch (e) { /* partial/garbled line */ }
      }
    }
  }
  return null;
}

async function uploadRequest(path, blob, field, filename) {
  const headers = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const form = new FormData();
  form.append(field, blob, filename);
  const resp = await fetch(`${BASE}/api${path}`, { method: 'POST', headers, body: form });
  let data = null;
  try { data = await resp.json(); } catch (e) { /* empty body */ }
  if (!resp.ok) {
    const err = new ApiError(resp.status, data?.detail ?? data);
    err.message = formatDetail(data?.detail ?? data);
    throw err;
  }
  return data;
}

export default api;
